import { userPath } from '../../shared/paths';

/**
 * Saving an edit from the Edit User page (PD-4068).
 *
 * There is no matching read: a single record has nothing to page or re-sort, so its
 * payload is embedded in the page's props by Management::UsersController#edit and
 * this answers with the re-read record.
 */

// The management layout emits csrf_meta_tags, so the token is on the page. Same
// approach as pages/Users/services/ssoAssignmentService.js.
const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

// The messages a failed response carries, or null if it has none to offer — an HTML
// error page, a proxy timeout or an empty body all land here.
const errorsFrom = async (response) => {
  try {
    const { errors } = await response.json();

    return errors && Object.keys(errors).length ? errors : null;
  } catch (error) {
    return null;
  }
};

/**
 * @returns {Promise<{ user?: object, errors?: object }>} `user` on success, or
 *   `errors` when the server sent messages to show — keyed by form field name, with
 *   anything the form has no field for under `base`. A rejected record is an expected
 *   outcome of a form, not an exception, so it comes back as a value; so is the 403 a
 *   viewer without user-management:Modify gets by posting around the disabled form,
 *   since it carries a reason worth reading. A failure with nothing to say throws.
 */
const updateUser = async (id, attributes) => {
  const response = await fetch(userPath(id), {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify({ user: attributes }),
  });

  if (!response.ok) {
    const errors = await errorsFrom(response);

    if (errors) {
      return { errors };
    }

    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const { user } = await response.json();

  return { user };
};

export default updateUser;
